// Feature: business-coverage-from-quote
// Client-side FormData builder + file guard for the "Generate Business Coverage
// Summary from Quote" flow. The validation mirrors the backend validator in
// `controllers/policy/business.controller.js` so the user sees an error before a
// round-trip (10 MB cap, allowed mime/extension set).

import { IGenerateCoverageFromQuoteArgs } from "../redux/businessPolicy/businessPolicyCoverage/coverageFromQuote.types";

export const ALLOWED_MIME = new Set<string>([
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/jpg",
  "text/csv",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
]);

export const ALLOWED_EXT_RE = /\.(pdf|png|jpe?g|csv|xlsx)$/i;

export const MAX_SIZE_BYTES = 10 * 1024 * 1024;

/**
 * Returns an error message string when the file is invalid, otherwise `null`.
 * A browser may report an empty `type` for some files, so the extension check
 * acts as a fallback when the mime type is missing/unknown.
 */
export const validateQuoteFile = (f: File): string | null => {
  if (f.size > MAX_SIZE_BYTES) {
    return `File ${f.name} exceeds 10 MB.`;
  }
  const mimeOk = ALLOWED_MIME.has(f.type);
  const extOk = ALLOWED_EXT_RE.test(f.name);
  if (!mimeOk && !extOk) {
    return `Unsupported file type: ${f.name} (${f.type || "unknown"}).`;
  }
  return null;
};

/**
 * Builds the multipart/form-data body for the generate-from-quote request.
 * Only the fields relevant to the selected source are appended. Linkage keys
 * are appended when present; the backend enforces the customer_policy_id XOR
 * lead_id rule.
 */
export const buildCoverageFromQuoteFormData = (
  args: IGenerateCoverageFromQuoteArgs
): FormData => {
  const fd = new FormData();

  if (args.customer_policy_id) {
    fd.append("customer_policy_id", String(args.customer_policy_id));
  }
  if (args.lead_id) {
    fd.append("lead_id", args.lead_id);
  }

  if (args.source === "UPLOAD" && args.file) {
    fd.append("files", args.file);
  }
  if (args.source === "S3_KEY" && args.quote_s3_key) {
    fd.append("quote_s3_key", args.quote_s3_key);
  }
  if (args.source === "MARKETING" && args.marketing_serial_number) {
    fd.append(
      "marketing_serial_number",
      String(args.marketing_serial_number)
    );
  }

  if (args.risk_location != null) {
    fd.append("risk_location", String(args.risk_location));
  }
  if (args.created_from) {
    fd.append("created_from", args.created_from);
  }
  if (args.reference_form_id) {
    fd.append("reference_form_id", args.reference_form_id);
  }

  return fd;
};
